import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from 'Components/DesignSystem/Alert/Alert';
import Button from 'Components/DesignSystem/Button/Button';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import { DEBOUNCE } from 'Components/DesignSystem/debounce';
import ConfigFieldRow from './ConfigFieldRow';
import { useResolveConfig } from '../context/ResolveConfigContext';
import { getProductCardById, getProductCardOptionsPage, OptionValue } from '../services/ResolveConfigService';

/**
 * The explicit "no card" row, always first — a channel's real card uids are
 * 32-character hex (`generate_uid_hex()`), so `''` can never collide with one.
 */
const NONE_OPTION: OptionValue = { value: '', label: 'Select' };

/** The only row shown when the Environment has no product cards at all — the control is frozen on it. */
const NO_CARDS_OPTION: OptionValue = { value: '', label: 'None' };

/**
 * The header's "Product card" affordance — a single dropdown setting opened in a
 * Dialog rather than a Configuration accordion.
 *
 * Options page in via `getProductCardOptionsPage` as the Dropdown's listbox is
 * scrolled (`onScrollEnd`) — a channel can hold far more cards than fit in one
 * fetch, so this never loads the full list up front. A search resets the page
 * cursor and the accumulated rows; it does not append to them.
 */
const ResolveProductCardButton: React.FC = () => {
  const {
    overrides,
    setOverride,
    saving,
    isProductCardDirty,
    saveProductCard,
    resetProductCard,
    channelKey,
    canUpdate,
  } = useResolveConfig();
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<OptionValue[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loadingOptions, setLoadingOptions] = useState(false);
  /** True once the unfiltered first page reports `total: 0` — the Environment has no cards
   * at all, so there is nothing to pick. A search that matches nothing does NOT set this;
   * it only proves the query missed, not that the Environment is empty. */
  const [noCardsInEnvironment, setNoCardsInEnvironment] = useState(false);
  /** The saved selection's own `{value, label}`, fetched by id — see `getProductCardById`.
   * Needed because the id alone carries no name, and the saved card may sit on a page
   * `getProductCardOptionsPage` hasn't loaded (or will never load, under an active search). */
  const [selectedCardOption, setSelectedCardOption] = useState<OptionValue | null>(null);

  const productCardId = typeof overrides.product_card_id === 'string' ? overrides.product_card_id : null;
  const hasMore = options.length < total;
  // The loaded page already carries the selected option once its own row is fetched
  // (e.g. it's an early result, or the user just picked it from the open list) — only
  // prepend the by-id lookup when it isn't already present, so it never shows twice.
  const dropdownOptions = useMemo(() => {
    const cardOptions =
      !selectedCardOption || options.some((option) => option.value === selectedCardOption.value)
        ? options
        : [selectedCardOption, ...options];
    return [NONE_OPTION, ...cardOptions];
  }, [options, selectedCardOption]);

  const fetchPage = useCallback(
    (pageNum: number, query: string, reset: boolean) => {
      if (!channelKey) return;
      setLoadingOptions(true);
      getProductCardOptionsPage(channelKey, { page: pageNum, search: query })
        .then(({ rows, total: totalRows }) => {
          setTotal(totalRows);
          if (pageNum === 1 && query === '') setNoCardsInEnvironment(totalRows === 0);
          setOptions((prev) => (reset ? rows : [...prev, ...rows]));
        })
        .finally(() => setLoadingOptions(false));
    },
    [channelKey],
  );

  useEffect(() => {
    if (!isOpen) return;
    setPage(1);
    setSearch('');
    setNoCardsInEnvironment(false);
    fetchPage(1, '', true);
    // Re-fetch from page 1 every time the dialog opens, so a stale list from an
    // earlier Environment or search never lingers behind the button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, channelKey]);

  useEffect(() => {
    if (!isOpen || !channelKey || !productCardId) {
      setSelectedCardOption(null);
      return undefined;
    }
    let cancelled = false;
    getProductCardById(channelKey, productCardId)
      .then((option) => {
        if (!cancelled) setSelectedCardOption(option);
      })
      // Falls back to no resolved label — same as before this fix, not worse.
      .catch(() => {
        if (!cancelled) setSelectedCardOption(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, channelKey, productCardId]);

  /** Cancel, ×, Escape — closing without saving discards the unsaved pick, so it never
   * lingers into the next open (or looks saved when it is not). */
  const closeDialog = useCallback(() => {
    resetProductCard();
    setIsOpen(false);
  }, [resetProductCard]);

  const handleScrollEnd = useCallback(() => {
    if (loadingOptions || !hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPage(nextPage, search, false);
  }, [loadingOptions, hasMore, page, search, fetchPage]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      setPage(1);
      fetchPage(1, value, true);
    },
    [fetchPage],
  );

  return (
    <>
      <Button
        variant="secondary"
        className="!tw-border-accent"
        onClick={() => setIsOpen(true)}
        data-testid="resolve-product-card-btn"
      >
        Product card
      </Button>
      <Dialog
        isOpen={isOpen}
        onClose={closeDialog}
        title="Product card"
        size="md"
        data-testid="resolve-product-card-dialog"
        footer={
          <div className="tw-flex tw-items-center tw-justify-end tw-gap-3">
            <Button
              variant="secondary"
              onClick={closeDialog}
              disabled={saving}
              data-testid="resolve-product-card-cancel-btn"
            >
              Cancel
            </Button>
            {canUpdate && (
              <Button
                onClick={saveProductCard}
                disabled={!isProductCardDirty || saving}
                loading={saving}
                data-testid="resolve-product-card-save-btn"
              >
                Save
              </Button>
            )}
          </div>
        }
      >
        {/* No label/tooltip column here — the dialog's own title ("Product card")
            already says what the one setting is; a "Product card" row label above
            the dropdown would just repeat it. */}
        <ConfigFieldRow testId="resolve-config-row-product_card_id" label="Product card" hideLabel>
          {noCardsInEnvironment ? (
            <div className="tw-flex tw-flex-col tw-gap-2">
              <Dropdown
                label=""
                options={[NO_CARDS_OPTION]}
                value={NO_CARDS_OPTION.value}
                disabled
                data-testid="resolve-config-row-product_card_id-control"
              />
              <Alert tone="info" data-testid="resolve-product-card-empty-info">
                No product cards yet — create one in Product Cards, then come back here to select it.
              </Alert>
            </div>
          ) : (
            <Dropdown
              label=""
              options={dropdownOptions}
              value={productCardId ?? NONE_OPTION.value}
              onChange={(next: string | number) =>
                setOverride('product_card_id', next === NONE_OPTION.value ? null : next)
              }
              disabled={saving}
              searchable
              usePortal
              loading={loadingOptions}
              onScrollEnd={handleScrollEnd}
              onSearchChange={handleSearchChange}
              debounceMs={DEBOUNCE.search}
              searchPlaceholder="Search product cards"
              data-testid="resolve-config-row-product_card_id-control"
            />
          )}
        </ConfigFieldRow>
      </Dialog>
    </>
  );
};

export default ResolveProductCardButton;
